import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  ArrowRight,
  CalendarDays,
  ShieldCheck,
  FileQuestion,
  LifeBuoy,
  Megaphone,
  ShoppingBag,
  ClipboardList,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Small primitives (kept local to avoid touching the Assessments dashboard) ----

const DiagKPICard = ({ label, value, sub, accent = "#0f172a", dataTestid }) => (
  <div
    className="rounded-xl bg-white border border-[#e5e7eb] p-5 transition-shadow hover:shadow-sm"
    data-testid={dataTestid}
  >
    <div className="text-[11.5px] font-semibold tracking-[0.14em] text-[#4b5563] uppercase">
      {label}
    </div>
    <div
      className="mt-2 text-[34px] font-bold tracking-[-0.02em] leading-none"
      style={{ color: accent }}
    >
      {value}
    </div>
    {sub && <div className="mt-3 text-[12px] text-[#64748b]">{sub}</div>}
  </div>
);

const QuickLinkTile = ({ icon: Icon, title, subtitle, to, accent = "#3B4FE4", dataTestid }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      className="group flex items-start gap-4 rounded-xl border border-[#e5e7eb] bg-white p-5 text-left transition-all hover:border-[#cbd5e1] hover:shadow-sm"
      data-testid={dataTestid}
    >
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
        style={{ background: `${accent}15`, color: accent }}
      >
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[14px] font-bold text-[#0f172a]">{title}</span>
          <ArrowRight className="h-4 w-4 text-[#4b5563] transition-transform group-hover:translate-x-0.5" />
        </div>
        <div className="mt-1 text-[12px] text-[#64748b]">{subtitle}</div>
      </div>
    </button>
  );
};

// ---- Main Diagnostics dashboard ------------------------------------------

export default function AdminDiagnosticsDashboard({ session }) {
  const [licenses, setLicenses] = useState([]);
  const [serviceInquiries, setServiceInquiries] = useState([]);
  const [pendingSignups, setPendingSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [revPeriod, setRevPeriod] = useState("this_month");

  const headers = useMemo(() => ({
    "x-tenant-id": session?.tenant?.id || "tenant_1",
    "x-user-id": session?.user?.id || "admin",
    "x-user-role": session?.user?.role || "admin",
  }), [session]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${BACKEND_URL}/api/admin/licenses?product_type=caporilli_diagnostic`, { headers })
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []),
      fetch(`${BACKEND_URL}/api/admin/service-inquiries`, { headers })
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []),
      fetch(`${BACKEND_URL}/api/admin/pending-signups`, { headers })
        .then((r) => (r.ok ? r.json() : []))
        .catch(() => []),
    ])
      .then(([lic, inq, signups]) => {
        setLicenses(Array.isArray(lic) ? lic : []);
        setServiceInquiries(Array.isArray(inq) ? inq : []);
        setPendingSignups(Array.isArray(signups) ? signups : []);
      })
      .finally(() => setLoading(false));
  }, [headers]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/admin/commission/overview?period=${revPeriod}`, { headers })
      .then((r) => (r.ok ? r.json() : null)).then(setOverview).catch(() => setOverview(null));
  }, [headers, revPeriod]);

  const totalLicenses = licenses.length;
  const activeLicenses = licenses.filter((l) => {
    const s = (l.effective_status || l.status || "").toLowerCase();
    return ["active", "manually_activated", "paid_active", "renewal_due", "grace_period"].includes(s);
  }).length;
  const expiringSoon = licenses.filter((l) => {
    const s = (l.effective_status || l.status || "").toLowerCase();
    return ["renewal_due", "grace_period", "pending_payment"].includes(s);
  }).length;

  const openInquiries = serviceInquiries.filter((i) => {
    const s = (i.status || "").toLowerCase();
    return ["new", "contacted", "proposal_sent"].includes(s);
  }).length;

  const pendingPOSignups = pendingSignups.filter((s) => s.signup_status === "po_pending_review").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="diag-dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#4b5563]" />
      </div>
    );
  }

  return (
    <div className="space-y-7" data-testid="admin-diagnostics-dashboard">
      {/* KPI row */}
      <div>
        <div className="text-[11.5px] font-semibold tracking-[0.16em] text-[#4b5563] uppercase mb-3">
          MySchoolRx — Operational Snapshot
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          <DiagKPICard
            label="Total Licenses"
            value={totalLicenses}
            sub="MySchoolRx accounts"
            accent="#0f172a"
            dataTestid="diag-kpi-total-licenses"
          />
          <DiagKPICard
            label="Active Licenses"
            value={activeLicenses}
            sub="Currently entitled tenants"
            accent="#16a34a"
            dataTestid="diag-kpi-active-licenses"
          />
          <DiagKPICard
            label="Expiring / Renewal Due"
            value={expiringSoon}
            sub="Need attention this cycle"
            accent="#C79A3B"
            dataTestid="diag-kpi-expiring-licenses"
          />
          <DiagKPICard
            label="Open Service Inquiries"
            value={openInquiries}
            sub={`${serviceInquiries.length} total submitted`}
            accent="#3B4FE4"
            dataTestid="diag-kpi-open-inquiries"
          />
        </div>
      </div>

      {/* Diagnostic Revenue Overview (rolls up from Sales employees) */}
      <div data-testid="diag-revenue-overview">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11.5px] font-semibold tracking-[0.16em] text-[#4b5563] uppercase">
            Diagnostic Revenue Overview
          </div>
          <select value={revPeriod} onChange={(e) => setRevPeriod(e.target.value)}
            className="h-8 rounded-md border border-slate-200 text-[12px] px-2 bg-white" data-testid="revenue-period-select">
            {[["this_week","This Week"],["last_week","Last Week"],["this_month","This Month"],["last_month","Last Month"],["this_quarter","This Quarter"],["last_quarter","Last Quarter"],["this_year","This Year"],["last_year","Last Year"],["all","All Time"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {(() => {
          const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
          const t = overview?.totals || {};
          const tiles = [
            ["Revenue Received", usd(t.revenue_received), "#16a34a"],
            ["Open Pipeline", usd(t.open_pipeline), "#3B4FE4"],
            ["Earned Commission", usd(t.earned_commission), "#0f172a"],
            ["Pending Commission", usd(t.pending_commission), "#C79A3B"],
            ["Pipeline Comm. Exposure", usd(t.pipeline_commission_exposure), "#7c3aed"],
            ["Renewals Due", t.renewals_due ?? 0, "#0f172a"],
          ];
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                {tiles.map(([label, val, accent]) => (
                  <div key={label} className="rounded-2xl border border-[#e6eaf0] bg-white p-4" data-testid={`revenue-tile-${label.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                    <div className="text-[11.5px] font-semibold tracking-[0.12em] text-[#4b5563] uppercase">{label}</div>
                    <div className="mt-2 text-2xl font-bold" style={{ color: accent }}>{val}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 rounded-2xl border border-[#e6eaf0] bg-white overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead><tr className="text-left text-[11.5px] uppercase tracking-wide text-[#4b5563] border-b border-[#eef1f5]">
                    <th className="p-3">Sales Employee</th><th className="p-3">Leads</th><th className="p-3">Open Pipeline</th><th className="p-3">Revenue Received</th><th className="p-3">Earned Comm.</th><th className="p-3">Pending Comm.</th><th className="p-3">Paid Comm.</th><th className="p-3">Renewals</th><th className="p-3">Active Lic.</th>
                  </tr></thead>
                  <tbody>
                    {(overview?.by_employee || []).length === 0 ? (
                      <tr><td colSpan={9} className="p-8 text-center text-[#4b5563]">No attributed revenue in this period.</td></tr>
                    ) : overview.by_employee.map((r) => (
                      <tr key={r.employee_id} className="border-b border-[#f3f5f8]" data-testid={`revenue-emp-${r.employee_id}`}>
                        <td className="p-3 font-medium text-[#0f172a]">{r.name} <span className="text-[11.5px] text-[#4b5563]">({r.rate_percent}%)</span></td>
                        <td className="p-3 text-[#475569]">{r.leads}</td>
                        <td className="p-3 text-[#475569]">{usd(r.open_pipeline)}</td>
                        <td className="p-3 text-[#475569]">{usd(r.revenue_received)}</td>
                        <td className="p-3 font-semibold text-emerald-700">{usd(r.earned_commission)}</td>
                        <td className="p-3 text-[#C79A3B]">{usd(r.pending_commission)}</td>
                        <td className="p-3 text-[#475569]">{usd(r.paid_commission)}</td>
                        <td className="p-3 text-[#475569]">{r.renewals_due}</td>
                        <td className="p-3 text-[#475569]">{r.active_licenses}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11.5px] text-[#4b5563]">Commission is approximate (before deductions/taxes) and excludes test data, cancelled and refunded records.</p>
            </>
          );
        })()}
      </div>

      {/* Quick links */}
      <div>
        <div className="text-[11.5px] font-semibold tracking-[0.16em] text-[#4b5563] uppercase mb-3">
          Quick Access
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickLinkTile
            icon={CalendarDays}
            title="Licenses"
            subtitle="Manage MySchoolRx annual licenses and renewals"
            to="/app/myschoolrx/licenses"
            accent="#16a34a"
            dataTestid="diag-quick-licenses"
          />
          <QuickLinkTile
            icon={LifeBuoy}
            title="Service Enquiries"
            subtitle={`${openInquiries} open · ${serviceInquiries.length} total`}
            to="/app/myschoolrx/service-enquiries"
            accent="#3B4FE4"
            dataTestid="diag-quick-service-inquiries"
          />
          <QuickLinkTile
            icon={ShieldCheck}
            title="Terms & Conditions"
            subtitle="Version, publish, and audit acceptance"
            to="/app/myschoolrx/terms"
            accent="#C79A3B"
            dataTestid="diag-quick-terms"
          />
          <QuickLinkTile
            icon={FileQuestion}
            title="Indicator Help Text"
            subtitle="Edit board-ready guidance for all 115 indicators"
            to="/app/myschoolrx/help-text"
            accent="#0891b2"
            dataTestid="diag-quick-help-text"
          />
          <QuickLinkTile
            icon={Megaphone}
            title="Campaigns"
            subtitle="Manage outreach campaigns, school directories, and AI-assisted research notes."
            to="/app/myschoolrx/campaigns"
            accent="#9333ea"
            dataTestid="diag-quick-campaigns"
          />
          <QuickLinkTile
            icon={ShoppingBag}
            title="Pending Signups"
            subtitle={pendingPOSignups > 0 ? `${pendingPOSignups} PO signups awaiting review` : "Approve or reject incoming MySchoolRx tenants"}
            to="/app/myschoolrx/signups"
            accent="#dc2626"
            dataTestid="diag-quick-products"
          />
        </div>
      </div>

      {/* Pending signups quick view */}
      {pendingPOSignups > 0 && (
        <div
          className="rounded-2xl border bg-white p-6"
          style={{ borderColor: "#fde68a", backgroundColor: "#fffbeb" }}
          data-testid="diag-pending-signups-card"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full" style={{ backgroundColor: "#C79A3B" }}>
              <ClipboardList className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-[14px] font-bold text-[#0f172a]">
                {pendingPOSignups} PO signup{pendingPOSignups === 1 ? "" : "s"} awaiting review
              </div>
              <div className="mt-1 text-[12px] text-[#475569]">
                Approve or reject incoming MySchoolRx tenants from Pending Signups.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
